import { ReactNode } from 'react'
import * as sumBy from 'lodash/sumBy'
import * as curry from 'lodash/curry'
import * as isNumber from 'lodash/isNumber'
import * as mergeWith from 'lodash/mergeWith'
import * as sortBy from 'lodash/sortBy'
import * as orderBy from 'lodash/orderBy'
import { CreditAccount } from 'interfaces/credit'
import { AccountCategory, AccountCategoryId, Category, CategoryId, CollectionCategory, DebtAssetCategory, DebtAssetCategoryId } from 'interfaces/meta'
import { CATEGORY_WORDING_MAP, NO_INTEREST_CATEGORIES } from 'constants/accounts'
import {
  AccountCollection,
  AccountsAmounts,
  AccountType,
  AggregatedBalanceByCategory,
  BalanceType,
  BaseCategoryInterest,
  CategoryInterest,
  CollectionCategoryInterest,
  DetailedAccount,
  OptionalRateAccount,
  RatedAccount,
} from 'interfaces/accounts'
import { ALL_ACCOUNT_CATEGORY_TITLE, ALL_CATEGORY_ID } from 'constants/meta'
import NoAccountsCopy from 'components/molecules/NoAccountsCopy'
import { SortingSelector } from 'components/molecules/tables/Table'
import { normalize } from 'helpers/normalizeHelpers'
import { LinkedMixedAccount, ProviderAccountStatesCollection } from 'interfaces/linkage'
import { NOT_FINISHED_STATUSES } from 'constants/providers'
import { PERFORM_MIXED_ACCOUNT_OPERATION, POLL_PROVIDER_ACCOUNT } from 'actions/linkageActions'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { Action } from 'actions'

const BALANCE_MAP: Record<CategoryId, BalanceType | null> = {
  savings: 'cash_balances',
  investment: 'investments',
  creditcard: 'credit_cards',
  debt_other: 'other_loans',
  mortgage: 'other_loans',
  home_equity: 'other_loans',
  debt_auto: 'other_loans',
  student: 'other_loans',
  cd: 'investments',
  asset_other: null,
  home: null,
  asset_auto: null,
}

export const DEFAULT_ACCOUNT_AMOUNTS: AccountsAmounts = {
  cash_balances: 0,
  investments: 0,
  credit_cards: 0,
  other_loans: 0,
}

export const getMemberAggregatedBalances = (accounts: AggregatedBalanceByCategory[]) => (
  accounts.reduce(
    (result: AccountsAmounts, item) => {
      const type = BALANCE_MAP[item.category]
      if (!type) {
        return result
      }
      result[type] += item.amount
      return result
    },
    { ...DEFAULT_ACCOUNT_AMOUNTS }
  )
)

export const groupAccountsByCategory = (accountsArray: CreditAccount[]) =>
  accountsArray.reduce((result: Record<CategoryId, AggregatedBalanceByCategory>, item) => {
    const existing = result[item.category.id]
    if (!existing) {
      result[item.category.id] = { category: item.category.id, amount: item.balance }
      return result
    }
    existing.amount += item.balance
    return result
  }, {
    all: { category: ALL_CATEGORY_ID, amount: sumBy(accountsArray, 'balance') },
  })

export const creditAccountsToBreakdown = (accounts: CreditAccount[], categories: CategoryId[]): AccountCollection => {
  const existing = accounts.reduce((result: Record<CategoryId, number>, account: CreditAccount) => {
    const { balance, category: { id: categoryId }} = account
    if (!result[categoryId]) {
      result[categoryId] = 0
    }
    // immutability does not matter here because we create collection here
    result[categoryId] += balance

    return result
  }, {
    [ALL_CATEGORY_ID]: sumBy(accounts, 'balance'),
  })

  return {
    allKeys: categories,
    byKey: categories.reduce((result: Record<CategoryId, AggregatedBalanceByCategory>, categoryId: CategoryId) => {
      result[categoryId] = {
        amount: existing[categoryId] || 0,
        category: categoryId,
      }

      return result
    }, {}),
  }
}

// ===========================================================================//
//                                RATES SECTION                               //
// ===========================================================================//

export const isFeeBased = (id: CategoryId) => ['asset_other', 'investment'].includes(id)

// TODO: join this method with `CompetitiveAccounts` component copies
export const getRateDescription = (categoryInterest: BaseCategoryInterest, type: AccountType): ReactNode => {
  const { allAccountsCount, category: { id }, competitiveAccountsCount, opportunitiesCount } = categoryInterest
  const isAssetCategory = type === 'assets'
  const V1 = CATEGORY_WORDING_MAP[id] ? CATEGORY_WORDING_MAP[id] : isAssetCategory ? 'asset' : 'debt'
  const V2 = isAssetCategory ? 'earn' : 'save'
  const V3 = isAssetCategory ? 'earning' : 'saving'
  const V4 = isAssetCategory ? 'earning' : 'paying'

  if (allAccountsCount === 1 && competitiveAccountsCount === 0) {
    return isFeeBased(id) ?
      `You paid fees on your ${ V1 } account. You may be able to reduce your fees by exploring different institutions.` :
      `The interest rate you're ${ V4 } on your ${ V1 } account is not competitive compared with your peers. See the opportunity below!`
  } else if (allAccountsCount === 1 && competitiveAccountsCount !== 0) {
    if (isFeeBased(id)) {
      return `You didn't pay any fees on your ${ V1 } account. That's great!`
    } else if (opportunitiesCount === 0) {
      return `The interest rate you're ${ V4 } on your ${ V1 } account is competitive compared with your peers. That's great!`
    } else {
      return `The interest rate you're ${ V4 } on your ${ V1 } account is competitive, but you could be ${ V3 } more. See the opportunity below!`
    }

  } else if (allAccountsCount > 1 && competitiveAccountsCount === allAccountsCount) {
    if (isFeeBased(id)) {
      return `You didn't pay any fees on your ${ V1 } accounts. That's great!`
    } else if (opportunitiesCount === 0) {
      return `The interest rates you're ${ V4 } on all of your ${ V1 } accounts are competitive compared with your peers. Well done!`
    } else if (opportunitiesCount === 1) {
      return `The interest rates you're ${ V4 } on all of your ${ V1 } accounts are competitive compared with your peers, but you could ${ V2 } more on 1 of them. See your opportunity below!`
    } else if (opportunitiesCount > 1) {
      return `The interest rates you're ${ V4 } on all of your ${ V1 } accounts are competitive compared with your peers, but you could ${ V2 } more on some of them. See your opportunities below!`
    } else {
      return `The interest rates you're ${ V4 } on all of your ${ V1 } accounts are competitively priced, but you could be ${ V3 } more. See your opportunities below!`
    }

  } else if (allAccountsCount > 1 && competitiveAccountsCount >= 1 && isFeeBased(id)) {
    return `You paid fees on ${ allAccountsCount - competitiveAccountsCount } out of your ${ allAccountsCount } ${ V1 } accounts. You may be able to reduce your fees by exploring different institutions.`
  } else if (allAccountsCount > 1 && competitiveAccountsCount === 1) {
    return `The interest rate you're ${ V4 } on 1 out of your ${ allAccountsCount } ${ V1 } accounts is competitive. You could ${ V2 } more - see your opportunities below!`
  } else if (allAccountsCount > 1 && competitiveAccountsCount > 1) {
    return `The interest rates you're ${ V4 } on ${ competitiveAccountsCount } out of your ${ allAccountsCount } ${ V1 } accounts are competitive. You could ${ V2 } more - see your opportunities below!`
  } else if (allAccountsCount > 1 && competitiveAccountsCount === 0) {
    return isFeeBased(id) ?
      `You paid fees on all of your ${ V1 } accounts. You may be able to reduce your fees by exploring different institutions.` :
      `The interest rates you're ${ V4 } on your ${ V1 } accounts are not competitive compared with your peers. See your opportunities below!`
  } else {
    return <NoAccountsCopy categoryId={ id } type={ type } standalone />
  }
}

export function isRatedAccount (account: RatedAccount | DetailedAccount | CreditAccount): account is RatedAccount {
  return Boolean((account as RatedAccount).rateObj)
}

export function isCreditAccount (account: RatedAccount | DetailedAccount | CreditAccount): account is CreditAccount {
  return Boolean((account as CreditAccount).credit_limit)
}


// such structure allows to prevent cases when new category is not considered here
const ACCOUNT_CATEGORY_ID_SET: Record<AccountCategoryId, true> = {
  creditcard: true,
  debt_other: true,
  debt_auto: true,
  mortgage: true,
  home_equity: true,
  student: true,
  savings: true,
  cd: true,
  investment: true,
  asset_other: true,
}
export function isAccountCategoryId (categoryId: DebtAssetCategoryId): categoryId is AccountCategoryId {
  return ACCOUNT_CATEGORY_ID_SET.hasOwnProperty(categoryId)
}

export function isAccountCategory (category: DebtAssetCategory): category is AccountCategory {
  return isAccountCategoryId(category.id)
}

export type AccountSortColumn = 'name' | 'account_type' | 'amount'

export const getAccountName = (account: { name: string }) => account.name

export const getAccountType = (account: { category: { value: string } }) => account.category.value

export const isManualAccount = (account: { is_manual?: boolean }) => Boolean(account.is_manual)

export const getAccountBalance = (account: DetailedAccount | CreditAccount): number => (
  'credit_limit' in account ? account.balance : (account.amount ?? 0)
)

export const sortAccounts = <T extends DetailedAccount | CreditAccount> (
  accounts: T[],
  column: AccountSortColumn,
  direction: 'asc' | 'desc'
): T[] => {
  if (column === 'name') {
    return orderBy(accounts, [(a) => a.name.toLowerCase()], [direction])
  }
  if (column === 'account_type') {
    return orderBy(accounts, [(a) => a.category.value.toLowerCase()], [direction])
  }
  return orderBy(accounts, [(a) => getAccountBalance(a)], [direction])
}

export const createAccountSortingSelector = curry((sortingSelector: SortingSelector<DetailedAccount>, account: DetailedAccount) => {
  if (account.link_status === 'unlinked') {
    return undefined
  }

  if (account.is_manual) {
    return null
  }

  return sortingSelector(account)
}) as (sortingSelector: SortingSelector<DetailedAccount>) => SortingSelector<DetailedAccount>

const createDefaultCategoryInterest = (category: Category): CategoryInterest => ({
  categoryId: category.id,
  category,
  interest: 0,
  feesPaid: 0,
  opportunitiesCount: 0,
  competitiveAccountsCount: 0,
  allAccountsCount: 0,
})

export const getAccountCategoryInterest = (account: OptionalRateAccount): CategoryInterest => {
  const categoryInterest: CategoryInterest = createDefaultCategoryInterest(account.category)

  if (!isRatedAccount(account)) {
    categoryInterest.competitiveAccountsCount++
  } else {
    const { rateObj } = account

    const opportunity = isNumber(rateObj.opportunity) ? rateObj.opportunity : 0
    const feesPaid = isNumber(rateObj.fees_paid) ? rateObj.fees_paid : 0
    const interest = isNumber(rateObj.interest) ? rateObj.interest : 0

    categoryInterest.interest += interest
    categoryInterest.feesPaid += feesPaid
    categoryInterest.opportunitiesCount += Number(opportunity > 0)
    categoryInterest.competitiveAccountsCount += Number(rateObj.is_competitive)
  }

  categoryInterest.allAccountsCount++

  return categoryInterest
}

type CategoryInterestMap = Record<CategoryId, CategoryInterest>
type CategoryInterestValue = CategoryInterest[keyof CategoryInterest]
const mergeWithCallback = (a: CategoryInterestValue, b: CategoryInterestValue): CategoryInterestValue =>
  isNumber(a) && isNumber(b) ? a + b : a

export const getCategoryInterests = (allAccounts: OptionalRateAccount[], categories: CollectionCategory): CollectionCategoryInterest => {
  // first we need to reduce existing account to interests
  const categoryInterestsMap = allAccounts.reduce<CategoryInterestMap>((result, account) => {
    if (NO_INTEREST_CATEGORIES.has(account.category.id)) {
      return result
    }

    const categoryInterest: CategoryInterest = result[account.category.id]
      || createDefaultCategoryInterest(account.category)

    const accountInterest = getAccountCategoryInterest(account)

    const nextCategoryInterest: CategoryInterest = mergeWith(
      categoryInterest,
      accountInterest,
      mergeWithCallback
    )

    return {
      ...result,
      [account.category.id]: nextCategoryInterest,
    }
  }, {})

  let allCategoryInterest: CategoryInterest = createDefaultCategoryInterest({
    id: ALL_CATEGORY_ID,
    value: ALL_ACCOUNT_CATEGORY_TITLE,
  })

  // second we need to walk thru all existing categories and add default interests where needed
  // besides that we calculate overall category interest (i.e. for `all` category)
  const metaCategoriesInterestsMap = categories.allKeys
    .reduce<CategoryInterestMap>(
      (result, categoryId) => {
        if (NO_INTEREST_CATEGORIES.has(categoryId)) {
          return result
        }

        if (categoryId === ALL_CATEGORY_ID) {
          return result
        }

        const currentCategoryInterest = result[categoryId]
          || createDefaultCategoryInterest(categories.byKey[categoryId])

        allCategoryInterest = mergeWith(
          allCategoryInterest,
          currentCategoryInterest,
          mergeWithCallback
        )

        return {
          ...result,
          [categoryId]: currentCategoryInterest,
          [ALL_CATEGORY_ID]: allCategoryInterest,
        }
      },
      categoryInterestsMap
    )

  const categoryInterestsArray = Object.keys(metaCategoriesInterestsMap).map((key) => metaCategoriesInterestsMap[key])
  const sortedInterestsArray = sortBy(categoryInterestsArray, ({ category }) => category.value.toLowerCase())

  return normalize(sortedInterestsArray, 'categoryId')
}


export const isMixedAccountInProgress =
  (states: ProviderAccountStatesCollection | null) => (mixedAccount: LinkedMixedAccount): boolean => {
    if (mixedAccount.is_manual || !states) {
      return false
    }

    const account = states.byKey[mixedAccount.cid]

    return Boolean(account && NOT_FINISHED_STATUSES.has(account.status))
  }

export const isPollingProviderAccountReady = (a: Action) =>
  a.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(a) && a.payload.response.providerAccountState.status === 'ready'

export const isAccountOperationSucceed = (a: Action) =>
  a.type === PERFORM_MIXED_ACCOUNT_OPERATION && isSuccessAction(a)
